import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
    FaShieldAlt, FaClock, FaCheckCircle, FaExclamationTriangle,
    FaTimesCircle, FaPauseCircle
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * SlaTrackingWidget
 *
 * Sidebar widget for the Action Item Detail page that displays SLA tracking
 * information for IT tickets. Shows response and resolution deadlines,
 * breach status, and elapsed time.
 *
 * The "time remaining" countdown re-calculates every 60 seconds so it stays
 * accurate without triggering an extra server fetch.
 *
 * Only renders for IT ticket types that have an SLA tracking record.
 */
const SlaTrackingWidget = ({ actionItemId }) => {
    const { apiFetch } = useAuth();
    const [tracking, setTracking] = useState(null);
    const [loading, setLoading] = useState(true);
    // Tick counter incremented every 60 s — causes a re-render so "time remaining"
    // labels are recalculated without an extra network request.
    const [, setTick] = useState(0);

    const loadTracking = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/ITServiceDesk/sla-tracking/${actionItemId}`);
            if (res.ok) {
                const json = await res.json();
                if (json.success) {
                    setTracking(json.data);
                } else {
                    setTracking(null);
                }
            } else {
                setTracking(null);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setTracking(null);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, actionItemId]);

    useEffect(() => { (async () => { await loadTracking(); })(); }, [loadTracking]);

    // Live countdown ticker — re-renders every 60 seconds so the "time remaining"
    // text updates without spamming the SLA tracking API endpoint.
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 60_000);
        return () => clearInterval(id);
    }, []);

    if (loading) return null; // Don't render loader for optional widget
    if (!tracking) return null; // No SLA tracking for this item

    const formatDeadline = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    /**
     * Calculate a human-readable "time remaining" string for a deadline.
     * The `_tick` parameter is passed from component state; its value is unused
     * in the computation but its presence in the call site ensures ESLint and
     * code reviewers can see that this function is tied to the 60-second
     * interval (re-called on every re-render triggered by setTick).
     */
    const getTimeRemaining = (deadlineStr) => {
        if (!deadlineStr) return null;
        const now = new Date();
        const deadline = new Date(deadlineStr);
        const diffMs = deadline - now;
        if (diffMs <= 0) return 'Overdue';
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
        return `${hours}h ${mins}m`;
    };

    const getStatusIcon = (breached, warned, completed) => {
        if (completed) return <FaCheckCircle className="sla-icon sla-met" />;
        if (breached) return <FaTimesCircle className="sla-icon sla-breached" />;
        if (warned) return <FaExclamationTriangle className="sla-icon sla-warning" />;
        return <FaClock className="sla-icon sla-active" />;
    };

    return (
        <div className="sla-tracking-widget">
            <div className="sla-widget-header">
                <FaShieldAlt />
                <span>SLA Tracking</span>
                {tracking.isPaused && (
                    <span className="sla-paused-badge"><FaPauseCircle /> Paused</span>
                )}
            </div>

            {/* Response SLA */}
            <div className={`sla-row ${tracking.responseBreached ? 'breached' : ''}`}>
                {getStatusIcon(tracking.responseBreached, tracking.responseWarned, !!tracking.firstResponseAt)}
                <div className="sla-row-info">
                    <span className="sla-row-label">First Response</span>
                    {tracking.firstResponseAt ? (
                        <span className="sla-row-value met">
                            {formatDeadline(tracking.firstResponseAt)}
                            {tracking.responseElapsedMinutes != null && (
                                <small> ({tracking.responseElapsedMinutes}m / {tracking.responseTargetMinutes}m target)</small>
                            )}
                        </span>
                    ) : (
                        <span className="sla-row-value">
                            Due: {formatDeadline(tracking.responseDeadline)}
                            {!tracking.isPaused && (
                                <small className={tracking.responseBreached ? 'overdue' : ''}>
                                    {' '}({getTimeRemaining(tracking.responseDeadline)})
                                </small>
                            )}
                        </span>
                    )}
                </div>
            </div>

            {/* Resolution SLA */}
            <div className={`sla-row ${tracking.resolutionBreached ? 'breached' : ''}`}>
                {getStatusIcon(tracking.resolutionBreached, tracking.resolutionWarned, !!tracking.resolvedAt)}
                <div className="sla-row-info">
                    <span className="sla-row-label">Resolution</span>
                    {tracking.resolvedAt ? (
                        <span className="sla-row-value met">
                            {formatDeadline(tracking.resolvedAt)}
                            {tracking.resolutionElapsedMinutes != null && (
                                <small> ({tracking.resolutionElapsedMinutes}m / {tracking.resolutionTargetMinutes}m target)</small>
                            )}
                        </span>
                    ) : (
                        <span className="sla-row-value">
                            Due: {formatDeadline(tracking.resolutionDeadline)}
                            {!tracking.isPaused && (
                                <small className={tracking.resolutionBreached ? 'overdue' : ''}>
                                    {' '}({getTimeRemaining(tracking.resolutionDeadline)})
                                </small>
                            )}
                        </span>
                    )}
                </div>
            </div>

            {tracking.totalPausedMinutes > 0 && (
                <div className="sla-paused-info">
                    Total paused: {tracking.totalPausedMinutes}m
                </div>
            )}
        </div>
    );
};

export default SlaTrackingWidget;
